import React, { useEffect, useState, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import ReactApexChart from 'react-apexcharts'

import { GET, httpOk } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'

const QUICK_FILTERS = [
	{ id: 'today', label: 'Bugun' },
	{ id: 'yesterday', label: 'Kecha' },
	{ id: 'week', label: '7 kun' },
	{ id: 'month', label: '30 kun' },
]

function getDateRange(id) {
	const now = new Date()
	const fmt = d => formatDate(d, 'yyyy-MM-dd')
	if (id === 'today') return { start: fmt(now), end: fmt(now) }
	if (id === 'yesterday') {
		const d = new Date(now); d.setDate(d.getDate() - 1)
		return { start: fmt(d), end: fmt(d) }
	}
	if (id === 'week') {
		const d = new Date(now); d.setDate(d.getDate() - 6)
		return { start: fmt(d), end: fmt(now) }
	}
	if (id === 'month') {
		const d = new Date(now); d.setDate(d.getDate() - 29)
		return { start: fmt(d), end: fmt(now) }
	}
	return { start: fmt(now), end: fmt(now) }
}

function BarProgress({ pct, color }) {
	return (
		<div style={{ height: 6, background: '#e9ecef', borderRadius: 3, marginTop: 4 }}>
			<div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .4s' }} />
		</div>
	)
}

export default function AnalyticsDashboard() {
	const now = new Date()
	const [activeFilter, setActiveFilter] = useState('month')
	const [startDate, setStartDate] = useState(new Date(now.getFullYear(), now.getMonth() - 1, now.getDate() + 1))
	const [endDate, setEndDate] = useState(now)
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(false)

	const load = useCallback(async (sd, ed) => {
		setLoading(true)
		const res = await GET('/analytics/dashboard', {
			start_date: formatDate(sd, 'yyyy-MM-dd'),
			end_date: formatDate(ed, 'yyyy-MM-dd'),
		}, { loader: false })
		setLoading(false)
		if (httpOk(res)) setData(res.data)
	}, [])

	useEffect(() => {
		const range = getDateRange('month')
		load(new Date(range.start), new Date(range.end))
	}, [load])

	function applyQuick(id) {
		setActiveFilter(id)
		const range = getDateRange(id)
		const s = new Date(range.start), e = new Date(range.end)
		setStartDate(s); setEndDate(e)
		load(s, e)
	}

	function applyRefresh(sd, ed) {
		setActiveFilter('')
		load(sd, ed)
	}

	const kpi = data?.kpi || {}
	const pipeline = data?.pipeline || {}
	const chart = data?.chart || { dates: [], sales: [], cheques: [] }
	const top = data?.top_products || {}

	const chartOptions = {
		chart: { type: 'line', toolbar: { show: false }, background: '#fff' },
		colors: ['#0d6efd', '#f59e0b'],
		stroke: { curve: 'smooth', width: [2, 2] },
		markers: { size: 3 },
		dataLabels: { enabled: false },
		legend: { show: true, position: 'top' },
		xaxis: { categories: chart.dates, labels: { rotate: -35, style: { fontSize: '11px' } }, tooltip: { enabled: false } },
		yaxis: [
			{ title: { text: "So'm" }, labels: { formatter: v => formatMoney(v, 0) } },
			{ opposite: true, title: { text: 'Buyurtma' }, labels: { formatter: v => v } },
		],
		tooltip: { shared: true },
	}

	const chartSeries = [
		{ name: 'Savdo', data: chart.sales, yAxis: 0 },
		{ name: 'Buyurtma', data: chart.cheques, yAxis: 1 },
	]

	const topColors = { profnastil: '#0d6efd', rulon: '#6f42c1', aksessuar: '#198754', tunukafon: '#dc3545', produkt: '#fd7e14' }
	const topLabels = { profnastil: 'Профнастил', rulon: 'Рулон', aksessuar: 'Аксессуар', tunukafon: 'Туникофон', produkt: 'Продукт' }

	return (
		<div className="an-page">
			{/* Header */}
			<div className="page-header d-flex justify-content-between align-items-center mb-3">
				<h4 className="mb-0">Analitika</h4>
			</div>

			{/* Filter bar */}
			<div className="card mb-3">
				<div className="card-body py-2 px-3">
					<div className="d-flex align-items-center gap-2 flex-wrap">
						{QUICK_FILTERS.map(f => (
							<button
								key={f.id}
								className={`btn btn-sm ${activeFilter === f.id ? 'btn-primary' : 'btn-outline-secondary'}`}
								onClick={() => applyQuick(f.id)}
							>{f.label}</button>
						))}
						<div style={{ width: 1, height: 28, background: '#dee2e6', margin: '0 4px' }} />
						<div className="calendar-input" style={{ width: 140 }}>
							<DatePicker
								className="form-control form-control-sm"
								dateFormat="dd.MM.yyyy"
								selected={startDate}
								onChange={d => { setStartDate(d); setActiveFilter('') }}
							/>
							<i className="uil uil-calendar-alt" />
						</div>
						<div className="calendar-input" style={{ width: 140 }}>
							<DatePicker
								className="form-control form-control-sm"
								dateFormat="dd.MM.yyyy"
								selected={endDate}
								onChange={d => { setEndDate(d); setActiveFilter('') }}
							/>
							<i className="uil uil-calendar-alt" />
						</div>
						<button className="btn btn-sm btn-success" onClick={() => applyRefresh(startDate, endDate)}>
							{loading
								? <span className="spinner-border spinner-border-sm" />
								: <><i className="uil uil-sync" /> Yangilash</>
							}
						</button>
					</div>
				</div>
			</div>

			{/* KPI Cards */}
			<div className="row g-3 mb-3">
				{[
					{ label: 'Savdo', value: formatMoney(kpi.total_sales), icon: 'uil-usd-circle', color: '#0d6efd', bg: '#e7f1ff' },
					{ label: 'Buyurtmalar', value: formatMoney(kpi.total_cheques, 0), icon: 'uil-file-check-alt', color: '#198754', bg: '#e8f5e9' },
					{ label: "O'rtacha buyurtma", value: formatMoney(kpi.avg_cheque), icon: 'uil-chart-line', color: '#6f42c1', bg: '#f3eeff' },
					{ label: 'Jami tonna', value: `${formatMoney(kpi.total_tonna, 3)} t`, icon: 'uil-weight', color: '#fd7e14', bg: '#fff3e0' },
					{
						label: 'Ялпи даромад',
						value: formatMoney(kpi.gross_income),
						icon: kpi.gross_income >= 0 ? 'uil-arrow-growth' : 'uil-arrow-down',
						color: kpi.gross_income >= 0 ? '#20c997' : '#dc3545',
						bg: kpi.gross_income >= 0 ? '#e0faf2' : '#fce4e4',
					},
					{ label: 'Chegirma', value: formatMoney(kpi.total_discount), icon: 'uil-percentage', color: '#fd7e14', bg: '#fff3e0' },
				].map((c, i) => (
					<div className="col-6 col-md-4 col-xl-2" key={i}>
						<div className="card h-100 mb-0" style={{ borderLeft: `3px solid ${c.color}` }}>
							<div className="card-body py-3 px-3">
								<div className="d-flex justify-content-between align-items-start">
									<div>
										<div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: .5 }}>{c.label}</div>
										<div className="fw-bold mt-1" style={{ fontSize: 18, color: c.color }}>{c.value}</div>
									</div>
									<div style={{ width: 36, height: 36, borderRadius: 8, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<i className={`${c.icon}`} style={{ fontSize: 20, color: c.color }} />
									</div>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>

			{/* Chart + Operatsion puls */}
			<div className="row g-3 mb-3">
				<div className="col-md-8">
					<div className="card h-100">
						<div className="card-body">
							<h6 className="card-title mb-3">Sotish dinamikasi</h6>
							<ReactApexChart
								key={JSON.stringify(chart.dates)}
								options={chartOptions}
								series={chartSeries}
								type="line"
								height={260}
							/>
						</div>
					</div>
				</div>
				<div className="col-md-4">
					<div className="card h-100">
						<div className="card-body">
							<h6 className="card-title mb-3">Operatsion puls</h6>
							<div className="d-flex flex-column gap-3">
								{[
									{ label: 'Ishlab chiqarishda', value: pipeline.in_production, color: '#f59e0b', icon: 'uil-cog' },
									{ label: 'Tayyor (omborda)', value: pipeline.ready, color: '#0d6efd', icon: 'uil-box' },
									{ label: "Yuborilgan (davr uchun)", value: pipeline.shipped, color: '#198754', icon: 'uil-truck' },
									{ label: 'Mijozlar qarzi', value: pipeline.total_debt, color: '#dc3545', icon: 'uil-bill', money: true },
									{ label: "Bugun yig'ildi", value: pipeline.today_collected, color: '#20c997', icon: 'uil-money-insert', money: true },
								].map((p, i) => (
									<div key={i} className="d-flex justify-content-between align-items-center p-2 rounded"
										style={{ background: '#f8f9fa' }}>
										<div className="d-flex align-items-center gap-2">
											<div style={{ width: 32, height: 32, borderRadius: 8, background: p.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
												<i className={p.icon} style={{ color: p.color, fontSize: 16 }} />
											</div>
											<span style={{ fontSize: 13 }}>{p.label}</span>
										</div>
										<span className="fw-bold" style={{ color: p.color, fontSize: p.money ? 14 : 18 }}>{p.money ? formatMoney(p.value) : (p.value ?? 0)}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Top products by type */}
			<div className="row g-3">
				{['profnastil', 'rulon', 'aksessuar', 'tunukafon', 'produkt'].map(key => {
					const group = top[key] || {}
					const items = group.data || []
					const color = topColors[key]
					return (
						<div className="col-md-4" key={key}>
							<div className="card h-100">
								<div className="card-body">
									<div className="mb-3">
										<h6 className="mb-0" style={{ color }}>Top {topLabels[key]}</h6>
										<small className="text-muted">Foyda ulushi bo'yicha</small>
									</div>
									{items.length === 0 ? (
										<div className="text-center text-muted py-4" style={{ fontSize: 13 }}>Ma'lumot yo'q</div>
									) : (
										<div className="d-flex flex-column gap-3">
											{items.map((item, i) => (
												<div key={i}>
													<div className="d-flex justify-content-between align-items-center mb-1">
														<div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
															<span style={{ fontWeight: 700, color, fontSize: 12, minWidth: 22 }}>#{i + 1}</span>
															<span className="text-truncate" style={{ fontSize: 13 }} title={item.name}>{item.name}</span>
														</div>
														<div className="d-flex align-items-center gap-2 ms-2" style={{ whiteSpace: 'nowrap' }}>
															<span className="text-muted" style={{ fontSize: 11 }}>
																{item.total_tonna > 0 ? `${Number(item.total_tonna).toFixed(3)} t` : `${Number(item.total_qty).toFixed(0)} dona`}
															</span>
															<span className="fw-bold" style={{ color, fontSize: 13 }}>{item.percentage}%</span>
														</div>
													</div>
													<BarProgress pct={item.percentage} color={color} />
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}
